<template>
    <ConfirmDialog />
    <Toast />
    <Spinner v-if="isLoading" />
    <div class="grid nested-grid">
        <div class="col-12">
            <div class="card fluid cardheader">
                <div class="col-12 header-actions-row">
                    <h5 class="stationsHeader">{{ translated('documentGenerationTitle') }}</h5>
                    <Button :label="translated('documentGenerationRefresh')" icon="pi pi-refresh" class="p-button" @click="loadJobs" />
                </div>
            </div>

            <DataTable :value="jobs" class="p-datatable-sm" showGridlines dataKey="Id"
                v-model:filters="filters1" :loading="loading1" responsiveLayout="scroll" :rowHover="true"
                :globalFilterFields="['statusText','progressText','filtersText','errorText']">
                <template #header>
                    <div class="flex justify-content-between align-items-center gap-2">
                        <Button :label="translated('addbutton')" icon="pi pi-plus" class="p-button" @click="openNewJob" />
                        <div class="search-refresh-wrap">
                            <i v-if="isRefreshingInBackground" class="pi pi-spin pi-spinner background-refresh-icon" />
                            <span class="p-input-icon-right">
                            <i class="pi pi-search" />
                            <InputText v-model="filters1['global'].value" :placeholder="translated('keywordsearch')" />
                            </span>
                        </div>
                    </div>
                </template>
                <template #empty>
                    {{ translated('documentGenerationEmpty') }}
                </template>
                <template #loading>
                    {{ translated('documentGenerationLoading') }}
                </template>

                <Column :header="translated('documentGenerationInserted')" :style="{width:'150px'}">
                    <template #body="slotProps">
                        {{ formatDate(slotProps.data.GenerationDateTime) }}
                    </template>
                </Column>

                <Column :header="translated('documentGenerationStatus')" field="statusText" :style="{width:'90px'}"></Column>

                <Column :header="translated('documentGenerationDocumentCount')" field="progressText" :style="{width:'100px'}"></Column>

                <Column :header="translated('documentGenerationFilters')" :style="{width:'32%'}">
                    <template #body="slotProps">
                        <div class="multiline-cell">{{ slotProps.data.filtersText }}</div>
                    </template>
                </Column>

                <Column :header="translated('documentGenerationError')" :style="{width:'38%'}">
                    <template #body="slotProps">
                        <div class="multiline-cell error-cell">{{ slotProps.data.errorText }}</div>
                    </template>
                </Column>

                <Column :exportable="false" :style="{width:'96px'}" headerStyle="text-align: center" bodyStyle="text-align: center">
                    <template #body="slotProps">
                        <div class="action-buttons">
                            <Button v-if="slotProps.data.Status === 5"
                                icon="pi pi-download"
                                class="p-button-sm p-button-rounded"
                                @click="downloadJob(slotProps.data)" />
                            <Button
                                icon="pi pi-trash"
                                class="p-button-sm p-button-danger p-button-rounded"
                                @click="deleteJob(slotProps.data)" />
                        </div>
                    </template>
                </Column>
            </DataTable>
        </div>
    </div>
</template>

<script>
import { FilterMatchMode } from 'primevue/api';
import createHttp from "@/services/http";
import { computed } from "vue";
import Spinner from "@/components/Spinner.vue";
import store from "@/store";

export default {
    name: 'DocumentGeneration',
    components: { Spinner },
    data() {
        return {
            translated: computed(() => store.getters.getTranslate),
            jobs: [],
            isLoading: false,
            loading1: true,
            filters1: null,
            refreshTimerId: null,
            isRefreshingInBackground: false
        };
    },
    created() {
        this.initFilters1();
    },
    async mounted() {
        await this.loadJobs();
        this.startRefreshTimer();
    },
    beforeUnmount() {
        if (this.refreshTimerId) {
            window.clearInterval(this.refreshTimerId);
            this.refreshTimerId = null;
        }
    },
    methods: {
        initFilters1() {
            this.filters1 = {
                global: { value: null, matchMode: FilterMatchMode.CONTAINS }
            };
        },
        startRefreshTimer() {
            if (this.refreshTimerId) {
                return;
            }

            this.refreshTimerId = window.setInterval(() => {
                this.refreshJobsInBackground();
            }, 10000);
        },
        stopRefreshTimer() {
            if (!this.refreshTimerId) {
                return;
            }

            window.clearInterval(this.refreshTimerId);
            this.refreshTimerId = null;
        },
        hasUnfinishedJobs(jobList) {
            return (jobList || []).some(x => x.Status === 0 || x.Status === 1);
        },
        getUnfinishedJobSignature(jobList) {
            return JSON.stringify((jobList || [])
                .filter(x => x.Status === 0 || x.Status === 1)
                .map(x => ({
                    Id: x.Id,
                    Status: x.Status,
                    RowProcessed: x.RowProcessed,
                    NumberOfRecords: x.NumberOfRecords,
                    ErrorString: x.ErrorString,
                    ZipFilePath: x.ZipFilePath,
                    LastUpdatedAt: x.LastUpdatedAt
                })));
        },
        openNewJob() {
            this.$router.push({ name: 'NewDocumentGeneration' });
        },
        getParameterCaseInsensitive(object, key) {
            return object[Object.keys(object).find(k => k.toLowerCase() === key.toLowerCase())]
        },
        async loadJobs(showSpinner = true) {
            if (showSpinner) {
                this.isLoading = true;
            } else {
                this.isRefreshingInBackground = true;
            }

            if (showSpinner) {
                this.loading1 = true;
            }

            try {
                const http = createHttp(true);
                const result = await http.get(process.env.VUE_APP_API_FOLDER + "/GetRefillPointDocumentGenerationJobs");

                if (result.status === 200) {
                    const newJobs = (result.data || []).map(item => this.mapJob(item));
                    this.jobs = newJobs;

                    if (this.hasUnfinishedJobs(newJobs)) {
                        this.startRefreshTimer();
                    } else {
                        this.stopRefreshTimer();
                    }
                }
            }
            finally {
                this.loading1 = false;
                this.isRefreshingInBackground = false;
                if (showSpinner) {
                    this.isLoading = false;
                }
            }
        },
        async refreshJobsInBackground() {
            if (this.isRefreshingInBackground || this.isLoading || !this.hasUnfinishedJobs(this.jobs)) {
                if (!this.hasUnfinishedJobs(this.jobs)) {
                    this.stopRefreshTimer();
                }
                return;
            }

            this.isRefreshingInBackground = true;

            try {
                const http = createHttp(true);
                const result = await http.get(process.env.VUE_APP_API_FOLDER + "/GetRefillPointDocumentGenerationJobs");

                if (result.status !== 200) {
                    return;
                }

                const newJobs = (result.data || []).map(item => this.mapJob(item));
                const oldSignature = this.getUnfinishedJobSignature(this.jobs);
                const newSignature = this.getUnfinishedJobSignature(newJobs);

                if (oldSignature !== newSignature) {
                    this.jobs = newJobs;
                }

                if (!this.hasUnfinishedJobs(newJobs)) {
                    this.stopRefreshTimer();
                }
            }
            finally {
                this.isRefreshingInBackground = false;
            }
        },
        async downloadJob(job) {
            this.isLoading = true;

            try {
                const http = createHttp(true);
                const result = await http.get(process.env.VUE_APP_API_FOLDER + "/DownloadRefillPointDocumentGenerationZip", {
                    params: { id: job.Id },
                    responseType: 'blob'
                });

                const tmpFileName = this.getParameterCaseInsensitive(result.headers, 'filename') || `document-generation-${job.Id}.zip`;
                const url = window.URL.createObjectURL(new Blob([result.data]));
                const link = document.createElement('a');

                link.href = url;
                link.setAttribute('download', tmpFileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }
            finally {
                this.isLoading = false;
            }
        },
        deleteJob(job) {
            this.$confirm.require({
                message: this.translated('documentGenerationDeleteQuestion'),
                header: this.translated('documentGenerationDeleteTitle'),
                icon: 'pi pi-exclamation-triangle',
                acceptClass: 'p-button-danger',
                accept: async () => {
                    this.isLoading = true;
                    try {
                        const http = createHttp(true);
                        const response = await http.delete(process.env.VUE_APP_API_FOLDER + "/DeleteRefillPointDocumentGenerationJob?id=" + job.Id);
                        if (response.status === 200) {
                            await this.loadJobs(false);
                            this.$toast.add({ severity:'success', summary:'Confirmed', detail:this.translated('documentGenerationDeleteSuccess'), life: 3000 });
                        }
                    }
                    catch (error) {
                        const detail = error?.response?.data?.message || this.translated('documentGenerationDeleteError');
                        this.$toast.add({ severity:'error', summary:'Error', detail, life: 5000 });
                    }
                    finally {
                        this.isLoading = false;
                    }
                }
            });
        },
        mapJob(item) {
            return {
                ...item,
                statusText: this.getStatusText(item.Status),
                progressText: this.getProgressText(item),
                filtersText: this.getFiltersText(item),
                errorText: item.ErrorString ?? ''
            };
        },
        getStatusText(status) {
            if (status === 0) return this.translated('documentGenerationStatusWaiting');
            if (status === 1) return this.translated('documentGenerationStatusRunning');
            if (status === 5) return this.translated('documentGenerationStatusFinished');
            if (status === 100) return this.translated('documentGenerationStatusError');
            return String(status ?? '');
        },
        getProgressText(item) {
            const processed = item.RowProcessed ?? 0;
            const total = item.NumberOfRecords ?? 0;
            return `${processed}/${total}`;
        },
        getFiltersText(item) {
            const lines = [];

            this.addFilterLine(lines, this.translated('documentGenerationFilterPublicPrivate'), this.getPublicPrivateText(item.PublicPrivate));
            this.addFilterLine(lines, this.translated('documentGenerationFilterCpoName'), item.CpoName);
            this.addFilterLine(lines, this.translated('documentGenerationFilterEvseId'), item.EvseId);
            this.addFilterLine(lines, this.translated('documentGenerationFilterUpravicenec'), item.Upravicenec);
            this.addFilterLine(lines, this.translated('documentGenerationFilterFinanced'), item.FinancedName);
            this.addFilterLine(lines, this.translated('documentGenerationFilterSnapshot'), this.formatDate(item.SnapshotDate));

            return lines.join('\n');
        },
        addFilterLine(lines, label, value) {
            if (value === null || value === undefined) {
                return;
            }

            const text = String(value).trim();
            if (text === '') {
                return;
            }

            lines.push(`${label} : ${text}`);
        },
        getPublicPrivateText(value) {
            if (value === null || value === undefined) {
                return '';
            }

            return value ? this.translated('documentGenerationPublic') : this.translated('documentGenerationPrivate');
        },
        formatDate(dateTime) {
            if (!dateTime) return '';

            const d = new Date(dateTime);
            return d.toLocaleString('sl-SI', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
        }
    }
}
</script>

<style scoped>
.multiline-cell {
    white-space: pre-line;
    word-break: break-word;
}

.error-cell {
    color: #b00020;
}

.action-buttons {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.header-actions-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.search-refresh-wrap {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.background-refresh-icon {
    color: #6b7280;
    font-size: 1rem;
}
</style>
